import * as angular from 'angular';
import * as d3 from 'd3';
import * as _ from 'lodash';
import * as moment from 'moment';

import { ComponentDefinition, TaskResult, ITaskUpdate, DateStepResult, BatchResult, TagDefinition, ITask, ITaskConfiguration, AcquistionTaskConfiguration, WorkflowTaskConfiguration, ConnectionPoint } from '../../../models';
import { IStepResult } from './IStepResult';
import {
    ICurrentApplicationService,
    IExportSvgService,
    ILoggerService,
    IRequestService,
    ISignalrService,
    IStoreService,
    ITaskService,
} from '../../../services';
import { StoreElement } from '../../../stateManagement';

class TaskProgressStateController implements angular.IComponentController {
    static controllerId = 'taskProgressState';

    data: TaskResult;
    isLoading: boolean;
    eta: string;
    showLogs: boolean;
    taskGuid: string;
    task: TaskResult;
    taskConfiguration: TaskResult;
    taskDefinition: ITaskConfiguration;
    toDraw: { stepName: string; results: any[] }[];

    tagGrouping: { connectionPoint: ConnectionPoint, tags: TagDefinition[] }[];

    private _format: (n: number) => string;
    private _taskGuid: string;
    private _proxy: SignalR.Hub.Proxy;
    private _handler: (report: ITaskUpdate) => void;

    static $inject = [
        '$q',
        '$scope',
        '$stateParams',
        '$timeout',
        'taskService',
        'loggerService',
        'currentApplicationService',
        'exportSvgService',
        'signalrService',
        'requestService',
        'storeService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _$scope: angular.IScope,
        private _$stateParams: angular.ui.IStateParamsService,
        private _$timeout: angular.ITimeoutService,
        private _taskService: ITaskService,
        private _loggerService: ILoggerService,
        private _currentApplicationService: ICurrentApplicationService,
        private _exportSvgService: IExportSvgService,
        private _signalrService: ISignalrService,
        private _requestService: IRequestService,
        private _storeService: IStoreService
    ) {
    }

    $onInit() {
        this._taskGuid = this._$stateParams.taskGuid;
        this._format = d3.format('2.2f');
        this.taskGuid = this._taskGuid;
        this.task = this.taskConfiguration;
        this.refresh()
            .finally(() => this._$timeout(() => this.initializeSignalr()));
        this._taskService.getAll(false)
            .then(data => {
                this.taskDefinition = _.find(data, d => d.guid === this.taskGuid);
                this._storeService.register(store => {
                    const tagDefinitions = this.taskDefinition && (this.taskDefinition as AcquistionTaskConfiguration).tagsList
                        ? (this.taskDefinition as AcquistionTaskConfiguration).tagsList.map(t => store.tags.dictionary[t])
                        : [];
                    const connectionPoints = store.connectionPoints.dictionary;
                    try {
                        this.tagGrouping = _.uniq(tagDefinitions.map(d => d.acquisitionPointGuid))
                            .map(d => {
                                return {
                                    connectionPoint: connectionPoints[d],
                                    tags: tagDefinitions
                                        .filter(t => t.acquisitionPointGuid === d)
                                        .sort((a, b) => a.name < b.name ? -1 : 1)
                                };
                            })
                            .sort((a, b) => a.connectionPoint?.name < b.connectionPoint?.name ? -1 : 1);
                    } catch (e) {
                        console.error(e);
                    }

                }, [StoreElement.Tags, StoreElement.ConnectionPoints]);
            });
    }

    private initializeSignalr(): void {
        const self = this;
        const proxy = this._signalrService.connection.createHubProxy('taskHub');
        this._signalrService.connect(() => {
            const app = this._currentApplicationService.getApplicationConfiguration();
            this._handler = (report: ITaskUpdate) => {
                if (self._taskGuid === report.TaskGuid && !this.isLoading) {
                    self.silentRefresh();
                }
            };
            proxy.on('push', this._handler);
            this._$scope.$on('$destroy', () => this._proxy.off('push', this._handler));
            this._proxy = proxy;
            proxy.invoke('joinWorkEnvironment', app.guid);
        });
    }

    refresh(): angular.IPromise<void> {
        if (this._taskGuid) {
            this.isLoading = true;
            return this
                .silentRefresh()
                .then(() => { /* nothing special */ })
                .finally(() => this._$timeout(() => this.isLoading = false));
        }
        return this._$q.resolve();
    }

    silentRefresh(): angular.IPromise<TaskResult> {
        return this._taskService
            .get(this._taskGuid)
            .then(data => {
                this.data = data;
                if (data.batchResults && data.batchResults.length > 0) {
                    const duration = this.getEta(data);
                    this.eta = this._format(duration.asMinutes());
                }
                return data;
            });
    }

    private getEta(data: TaskResult): moment.Duration {
        const dsr: DateStepResult[] = _(data.batchResults)
            .map(d => d.value)
            .flatten()
            .map((d: BatchResult) => d.stepResults)
            .flatten()
            .value();
        const byType = d3.nest<DateStepResult>()
            .key(d => d.stepName)
            .entries(dsr);
        const mapped = byType.map(kvp => {
            const currentStepResults: DateStepResult[] = kvp.values;
            const completed = currentStepResults.filter(d =>
                d.exitCode !== 0
                && d.exitCode !== 1
                && moment.duration(d.elapsed).asMilliseconds() > 0
            );
            if (completed.length > 0) {
                const completedCount = completed.length;
                const totalDuration = completed
                    .map(d => moment.duration(d.elapsed))
                    .reduce((pv, cv) => pv.add(cv));
                const remainingDuration = moment.duration(totalDuration.asMilliseconds() / completedCount
                    * currentStepResults.filter(d => d.exitCode === 0 || d.exitCode === 1).length);
                return remainingDuration;
            }
            return moment.duration(0);
        });
        return mapped.reduce((pv, cv) => pv.add(cv));
    }

    start(): void {
        this._taskService.start(this._taskGuid)
            .then(() => {
                this._loggerService.logSuccess('Task started.');
                this.refresh();
            })
            .catch(() => {
                this._loggerService.logError('Failed to start task.');
            });

    }

    cancel(): void {
        this._taskService.cancel(this._taskGuid)
            .then(() => {
                this._loggerService.logSuccess('Task cancelled.');
                this.refresh();
            })
            .catch(() => {
                this._loggerService.logError('Failed to cancel task.');
            });
    }

    onBatchClick(batch: BatchResult & { groupGuid: string }) {
        this._requestService.cleanCacheAtV1('task', {
            taskGuid: this.taskGuid,
            groupGuid: batch.groupGuid,
            beginDateTime: batch.range.beginDateTime,
            endDateTime: batch.range.endDateTime
        });
        this._requestService
            .getAtV1('task', {
                taskGuid: this.taskGuid,
                groupGuid: batch.groupGuid,
                beginDateTime: batch.range.beginDateTime,
                endDateTime: batch.range.endDateTime
            })
            .then((data: IStepResult[]) => {
                const steps = batch.stepResults.map(r => r.stepName);
                const byStep = _.groupBy(data, (d: IStepResult) => d.stepName);
                try {
                    this.toDraw = this.convertToDraw(steps, byStep);
                } catch (e) {
                    console.error(e);
                }
            });
    }

    exportToPng() {
        let svgElement: any = d3.select(`wt-task-progress`).select('svg').node();
        if (svgElement.length) {
            svgElement = svgElement[0];
        }
        return this._exportSvgService.exportSvgAsPng('Task progress', svgElement);

    }

    private convertToDraw(steps: string[], byStep: _.Dictionary<IStepResult[]>) {
        return steps.map(s => {
            return {
                stepName: s,
                results: byStep[s]
                    ? byStep[s].map(r => {
                        return {
                            timeStamp: new Date(r.information.timeStamp),
                            status: r.status,
                            message: r.message
                        };
                    })
                    : []
            };
        });
    }
}

export const wtTaskProgressState: ComponentDefinition = {
    componentId: 'wtTaskProgressState',
    controller: TaskProgressStateController,
    template: require('./taskProgressState.component.html'),
    controllerAs: 'vm',
    bindings: {
        taskConfiguration: '<'
    }
};